import React from 'react';
import { getCurrencySign } from '@/helpers/currency';
import moment from 'moment';
import { ClickToCopy } from '@monavate/ui-core';
import { StatusTableBage } from '@monavate/ui-core';
import { returnCorrectDateAndTimeFormat } from '../../helpers/returnCorrectDateFormat';
import { capitalizeFirstLetter } from '../../helpers/CapitalizeFirstLetter';
import CardNumberSecurity from '../CardNumberSecurity/CardNumberSecurity';
import { returnCardSchemeIcon } from '../../helpers/returnCardSchemeIcon';
import styled from 'styled-components';
import countryData from '../../assets/json/countries.json';
import { getCountryNameByCode } from '../../helpers/getCountryNameByCode';
import CardNumberLink from '../CardNumberLink/CardNumberLink';
import { findTransactionStatus } from '@/helpers/findTransactionStatus';
import { AmountFormat } from '@monavate/ui-core';
import _ from 'lodash';
import NumberFormat from 'react-number-format';
import LinkPaymentIcon from '@/components/LinkPaymentIcon/LinkPaymentIcon';
import { canLinkPayment } from '@/helpers/canLinkPayment';

const SchemaIconContainer = styled.div`
    margin-right: 12px;
`;

const TypeTagWrapper = styled.div`
    display: flex;
    padding: 1px 8px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    text-transform: capitalize;
    background: ${(props) => props.theme['Status/neutral/bg']};
`;

const CardSchemeBlock = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

// a falsy value hides the row, which is what bank transactions want: they never carry one
const renderUnlinkedPaymentInstruction = (transactionDetails: any) => {
    if (transactionDetails.type !== 'card') {
        return false;
    }
    return canLinkPayment(transactionDetails) ? (
        <LinkPaymentIcon transaction={transactionDetails} closesTransactionBlade />
    ) : (
        '-'
    );
};

export const createTransactionObject = (transactionDetails: any) => {
    const transactionIsBankType = transactionDetails.type === 'bank';
    const transactionStatus = findTransactionStatus(transactionDetails);
    const transactionAmount = transactionDetails?.amounts
        ? `${getCurrencySign(
              transactionDetails?.cardTransaction?.amounts?.transactionCurrency
                  ? transactionDetails.cardTransaction?.amounts?.transactionCurrency
                  : '',
          )} ${transactionDetails.cardTransaction?.amounts.transactionAmount ? transactionDetails.cardTransaction?.amounts?.transactionAmount : ''}`
        : '';
    const transactionBillingAmount = transactionDetails.cardTransaction?.amounts
        ? `${getCurrencySign(
              transactionDetails.cardTransaction?.amounts?.billingCurrency
                  ? transactionDetails.cardTransaction.amounts.billingCurrency
                  : '',
          )} ${transactionDetails.cardTransaction?.amounts?.billingAmount ? transactionDetails.cardTransaction.amounts.billingAmount : ''}`
        : '';
    const isTransactionRejected =
        transactionStatus === 'rejected' || transactionStatus === 'declined';
    const rejectReason = () => {
        if (
            transactionIsBankType &&
            (!!transactionDetails.bankTransaction.rejectReason ||
                !!transactionDetails.bankTransaction.rejectDescription)
        ) {
            return (
                transactionDetails.bankTransaction.rejectReason ||
                transactionDetails.bankTransaction.rejectDescription
            );
        }
        if (!transactionIsBankType && !!transactionDetails.cardTransaction.authDeclineReason) {
            return transactionDetails.cardTransaction.authDeclineReason;
        }
        return '';
    };
    const referenceNumber = _.get(
        transactionDetails,
        'cardTransaction.details.referenceNumber',
        '',
    );
    const originalReferenceNumber = _.get(
        transactionDetails,
        'cardTransaction.details.originalReferenceNumber',
        '',
    );
    const originalTransactionId = _.get(
        transactionDetails,
        'cardTransaction.details.originalTransactionId',
        '',
    );
    const authCode = _.get(transactionDetails, 'cardTransaction.details.authCode', '');
    const cardTransaction = _.get(transactionDetails, 'cardTransaction') || {};
    // `incremental` only carries meaning for authorisations, and False is as informative as True,
    // so the row shows whenever the API sends the flag on an authorisation.
    const incremental =
        cardTransaction.type === 'authorization' && cardTransaction.hasOwnProperty('incremental')
            ? capitalizeFirstLetter(cardTransaction.incremental.toString())
            : '';
    // `refundAuth` (MP-16262) is NOT authorisation-only: the API also sets it on a reversal of a
    // refund authorisation, so the row follows the flag on any card transaction that carries it.
    // As with `incremental`, False is as informative as True.
    const refundAuth = cardTransaction.hasOwnProperty('refundAuth')
        ? capitalizeFirstLetter(cardTransaction.refundAuth.toString())
        : '';
    return [
        {
            rows: [
                {
                    name: 'ID',
                    customComponentValue: (
                        <ClickToCopy
                            value={transactionDetails.id}
                            dataTestId="value-transaction-id"
                        >
                            {transactionDetails.id}
                        </ClickToCopy>
                    ),
                    showFull: true,
                },
                {
                    name: 'Payment instruction',
                    customComponentValue: transactionDetails.paymentId ? (
                        <ClickToCopy
                            value={transactionDetails.paymentId}
                            dataTestId="value-payment-instruction"
                        >
                            {transactionDetails.paymentId}
                        </ClickToCopy>
                    ) : (
                        renderUnlinkedPaymentInstruction(transactionDetails)
                    ),
                    showFull: true,
                },
                {
                    name: 'Order',
                    customComponentValue: transactionDetails.orderReference && (
                        <ClickToCopy dataTestId="value-order-reference">
                            {transactionDetails.orderReference}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Date & time',
                    customComponentValue: (
                        <ClickToCopy dataTestId="value-date-time">
                            {returnCorrectDateAndTimeFormat(moment(transactionDetails.createdAt))}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Status',
                    customComponentValue: (
                        <ClickToCopy
                            value={transactionStatus}
                            dataTestId="value-transaction-status"
                        >
                            <StatusTableBage
                                status={transactionStatus}
                                dataTestId="badge-transaction-status"
                            >
                                {transactionStatus}
                            </StatusTableBage>
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Reject reason',
                    customComponentValue: isTransactionRejected && (
                        <div style={{ whiteSpace: 'normal' }}>
                            <ClickToCopy value={rejectReason()} dataTestId="value-reject-reason">
                                {rejectReason()}
                            </ClickToCopy>
                        </div>
                    ),
                },
                {
                    name: 'Transaction amount',
                    customComponentValue: transactionDetails.cardTransaction?.amounts && (
                        <ClickToCopy
                            value={transactionAmount}
                            dataTestId="value-transaction-amount"
                        >
                            <AmountFormat
                                amount={
                                    transactionDetails?.cardTransaction?.amounts?.transactionAmount
                                }
                                currency={
                                    transactionDetails?.cardTransaction?.amounts
                                        ?.transactionCurrency
                                }
                                letters
                                dataTestId="amount-transaction"
                            />
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Billing amount',
                    customComponentValue: transactionBillingAmount && (
                        <ClickToCopy
                            value={transactionBillingAmount}
                            dataTestId="value-billing-amount"
                        >
                            <AmountFormat
                                amount={transactionDetails?.cardTransaction?.amounts?.billingAmount}
                                currency={
                                    transactionDetails?.cardTransaction?.amounts?.billingCurrency
                                }
                                letters
                                dataTestId="amount-billing"
                            />
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Fee',
                    value: transactionDetails.fee,
                    customComponentValue: transactionDetails.fee && (
                        <ClickToCopy
                            value={`${getCurrencySign(
                                transactionDetails.currency ? transactionDetails.currency : '',
                            )} ${transactionDetails.fee ? transactionDetails.fee : ''}`}
                            dataTestId="value-fee"
                        >
                            {`${getCurrencySign(
                                transactionDetails.currency ? transactionDetails.currency : '',
                            )} ${transactionDetails.fee ? transactionDetails.fee : ''}`}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Incremental',
                    customComponentValue: incremental && (
                        <ClickToCopy value={incremental} dataTestId="value-incremental">
                            {incremental}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Refund',
                    customComponentValue: refundAuth && (
                        <ClickToCopy value={refundAuth} dataTestId="value-refund-auth">
                            {refundAuth}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Reference number',
                    customComponentValue: referenceNumber && (
                        <ClickToCopy value={referenceNumber} dataTestId="value-reference-number">
                            {referenceNumber}
                        </ClickToCopy>
                    ),
                    showFull: true,
                },
                {
                    name: 'Original reference number',
                    customComponentValue: originalReferenceNumber && (
                        <ClickToCopy
                            value={originalReferenceNumber}
                            dataTestId="value-original-reference-number"
                        >
                            {originalReferenceNumber}
                        </ClickToCopy>
                    ),
                    showFull: true,
                },
                {
                    name: 'Original transaction ID',
                    customComponentValue: originalTransactionId && (
                        <ClickToCopy
                            value={originalTransactionId}
                            dataTestId="value-original-transaction-id"
                        >
                            {originalTransactionId}
                        </ClickToCopy>
                    ),
                    showFull: true,
                },
                {
                    name: 'Auth code',
                    customComponentValue: authCode && (
                        <ClickToCopy value={authCode} dataTestId="value-auth-code">
                            {authCode}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};

const bankPaymentTypeMap: Record<string, string> = {
    SEPA: 'SEPA',
    INST: 'SEPA Instant',
    FAT: 'Faster Payments',
    SWIFT: 'SWIFT',
};

export const createPaymentObject = (
    transactionDetails: any,
    paymentDetails: any,
    cardData: any,
) => {
    const type = paymentDetails?.requestType === 'bank' ? 'Bank' : 'Card';
    const cardName = transactionDetails.cardTransaction?.cardName;
    const schema =
        type === 'Card'
            ? cardData?.scheme
            : bankPaymentTypeMap[paymentDetails?.creditTransfer?.paymentType];
    const productType = cardData?.productType?.toUpperCase();
    return [
        {
            rows: [
                {
                    name: 'Type',
                    customComponentValue: cardData && (
                        <CardNumberLink cardData={cardData} type={type} />
                    ),
                },
                {
                    name: 'Card number',
                    customComponentValue: cardData && <CardNumberSecurity cardData={cardData} />,
                },
                {
                    name: 'Card name',
                    customComponentValue: cardName && (
                        <ClickToCopy value={cardName} dataTestId="value-card-name">
                            {cardName}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Scheme',
                    customComponentValue: schema && (
                        <CardSchemeBlock>
                            <SchemaIconContainer>
                                {returnCardSchemeIcon(schema)}
                            </SchemaIconContainer>
                            <div style={{ textTransform: 'capitalize' }}>
                                <ClickToCopy value={schema} dataTestId="value-scheme">
                                    {schema}
                                </ClickToCopy>
                            </div>
                        </CardSchemeBlock>
                    ),
                },
                {
                    name: 'Product type',
                    customComponentValue: productType && (
                        <ClickToCopy value={productType} dataTestId="value-product-type">
                            {productType}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};
export const createCardSupplierObject = (transactionDetails: any, paymentInfo: any) => {
    const cardMcc = _.get(transactionDetails, 'cardTransaction.cardAcceptor.mcc', '');
    const cardCountry = getCountryNameByCode(
        _.get(transactionDetails, 'cardTransaction.cardAcceptor.countryCode', ''),
        countryData,
    );
    const source = _.get(transactionDetails, 'cardTransaction.cardAcceptor.source', '');
    const type = _.get(transactionDetails, 'cardTransaction.cardAcceptor.type', '');
    const mid = _.get(transactionDetails, 'cardTransaction.cardAcceptor.mid', '');
    const tid = _.get(transactionDetails, 'cardTransaction.cardAcceptor.tid', '');
    const city = _.get(transactionDetails, 'cardTransaction.cardAcceptor.city', '');
    const state = _.get(transactionDetails, 'cardTransaction.cardAcceptor.state', '');
    const name = _.get(transactionDetails, 'cardTransaction.cardAcceptor.name', '');
    const supplierName = _.get(paymentInfo, 'supplierName', '');
    return [
        {
            rows: [
                {
                    name: 'Supplier name',
                    customComponentValue: supplierName && (
                        <ClickToCopy value={supplierName} dataTestId="value-card-supplier-name">
                            {supplierName}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Merchant name',
                    customComponentValue: name && (
                        <ClickToCopy value={name} dataTestId="value-merchant-name">
                            {name}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'MCC',
                    customComponentValue: cardMcc && (
                        <ClickToCopy value={cardMcc} dataTestId="value-mcc">
                            {cardMcc}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Country',
                    customComponentValue: cardCountry && (
                        <ClickToCopy value={cardCountry} dataTestId="value-card-country">
                            {cardCountry}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Source',
                    customComponentValue: source && (
                        <ClickToCopy value={source} dataTestId="value-source">
                            <TypeTagWrapper>{source}</TypeTagWrapper>
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Type',
                    customComponentValue: type && (
                        <ClickToCopy value={type} dataTestId="value-acceptor-type">
                            <TypeTagWrapper>{type}</TypeTagWrapper>
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'MID',
                    customComponentValue: mid && (
                        <ClickToCopy value={mid} dataTestId="value-mid">
                            {mid}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'TID',
                    customComponentValue: tid && (
                        <ClickToCopy value={tid} dataTestId="value-tid">
                            {tid}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'City',
                    customComponentValue: city && (
                        <ClickToCopy value={city} dataTestId="value-city">
                            {city}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'State',
                    customComponentValue: state && (
                        <ClickToCopy value={state} dataTestId="value-state">
                            {state}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};

export const createBankSupplierObject = (transactionDetails: any) => {
    const supplierName = transactionDetails.supplierName;
    const creditorOrDebtor =
        transactionDetails.bankTransaction?.bankTransactionType === 'bankTransfer'
            ? 'creditor'
            : 'debtor';
    const country = getCountryNameByCode(
        transactionDetails.bankTransaction?.[creditorOrDebtor]?.address?.country,
        countryData,
    );
    const IBAN = transactionDetails.bankTransaction?.[creditorOrDebtor]?.iban;
    const BIC = transactionDetails.bankTransaction?.[creditorOrDebtor]?.bankBIC;
    const accountNumber = transactionDetails.bankTransaction?.[creditorOrDebtor]?.accountNumber;
    const sortCode = transactionDetails.bankTransaction?.[creditorOrDebtor]?.sortCode;
    // console.log('transactionDetails ', transactionDetails);
    // console.log('BIC ', BIC);
    return [
        {
            rows: [
                {
                    name: 'Supplier name',
                    customComponentValue: supplierName && (
                        <ClickToCopy value={supplierName} dataTestId="value-bank-supplier-name">
                            {supplierName}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Country',
                    customComponentValue: country && (
                        <ClickToCopy value={country} dataTestId="value-bank-country">
                            {country}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'IBAN',
                    customComponentValue: IBAN && (
                        <ClickToCopy value={IBAN} dataTestId="value-iban">
                            {IBAN}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'BIC',
                    customComponentValue: BIC && (
                        <ClickToCopy value={BIC} dataTestId="value-bic">
                            {BIC}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Account number',
                    customComponentValue: accountNumber && (
                        <ClickToCopy value={accountNumber} dataTestId="value-account-number">
                            {accountNumber}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Sort code',
                    customComponentValue: sortCode && (
                        <ClickToCopy value={sortCode} dataTestId="value-sort-code">
                            {sortCode}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};

export const createAmountObject = (transactionDetails: any) => {
    const transactionAmount = _.get(
        transactionDetails,
        'cardTransaction.amounts.transactionAmount',
        '',
    );
    const transactionCurrency = _.get(
        transactionDetails,
        'cardTransaction.amounts.transactionCurrency',
        '',
    );
    const billingAmount = _.get(transactionDetails, 'cardTransaction.amounts.billingAmount', '');
    const billingCurrency = _.get(
        transactionDetails,
        'cardTransaction.amounts.billingCurrency',
        '',
    );
    return [
        {
            rows: [
                {
                    name: 'Amount',
                    customComponentValue: transactionAmount && (
                        <ClickToCopy value={transactionAmount} dataTestId="value-amount">
                            <NumberFormat
                                value={transactionAmount ? transactionAmount?.toFixed(2) : '0.00'}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={true}
                            />
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Currency',
                    customComponentValue: transactionCurrency && (
                        <ClickToCopy value={transactionCurrency} dataTestId="value-currency">
                            {transactionCurrency}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Billing amount',
                    customComponentValue: billingAmount && (
                        <ClickToCopy value={billingAmount} dataTestId="value-billing-amount-2">
                            <NumberFormat
                                value={billingAmount ? billingAmount?.toFixed(2) : '0.00'}
                                displayType={'text'}
                                decimalScale={2}
                                thousandSeparator={true}
                            />
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Billing currency',
                    customComponentValue: billingCurrency && (
                        <ClickToCopy value={billingCurrency} dataTestId="value-billing-currency">
                            {billingCurrency}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};

export const create3DSecureObject = (transactionDetails: any) => {
    const resultCode = _.get(transactionDetails, 'cardTransaction.secure3DS.resultCode', '');
    const method = _.get(transactionDetails, 'cardTransaction.secure3DS.method', '');
    const version = _.get(transactionDetails, 'cardTransaction.secure3DS.version', '');
    const reference = _.get(transactionDetails, 'cardTransaction.secure3DS.reference', '');

    return [
        {
            rows: [
                {
                    name: 'Result code',
                    customComponentValue: resultCode && (
                        <ClickToCopy value={resultCode} dataTestId="value-3ds-result-code">
                            {resultCode}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Method',
                    customComponentValue: method && (
                        <ClickToCopy value={method} dataTestId="value-3ds-method">
                            {method}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Version',
                    customComponentValue: version && (
                        <ClickToCopy value={version} dataTestId="value-3ds-version">
                            {version}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Reference',
                    customComponentValue: reference && (
                        <ClickToCopy value={reference} dataTestId="value-3ds-reference">
                            {reference}
                        </ClickToCopy>
                    ),
                    showFull: true,
                },
            ],
        },
    ];
};

export const createAcquirerObject = (transactionDetails: any) => {
    const institutionId = _.get(transactionDetails, 'cardTransaction.acquirer.institutionId', '');
    const systemTraceAuditNumber = _.get(
        transactionDetails,
        'cardTransaction.acquirer.systemTraceAuditNumber',
        '',
    );
    const retrievalId = _.get(transactionDetails, 'cardTransaction.acquirer.retrievalId', '');

    return [
        {
            rows: [
                {
                    name: 'Institution ID',
                    customComponentValue: institutionId && (
                        <ClickToCopy value={institutionId} dataTestId="value-institution-id">
                            {institutionId}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'System trace audit number',
                    customComponentValue: systemTraceAuditNumber && (
                        <ClickToCopy
                            value={systemTraceAuditNumber}
                            dataTestId="value-system-trace-audit-number"
                        >
                            {systemTraceAuditNumber}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Retrieval ID',
                    customComponentValue: retrievalId && (
                        <ClickToCopy value={retrievalId} dataTestId="value-retrieval-id">
                            {retrievalId}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};

export const createPOSObject = (transactionDetails: any) => {
    const posCondition = _.get(transactionDetails, 'cardTransaction.pos.posCondition', '');
    const posEntryMode = _.get(transactionDetails, 'cardTransaction.pos.posEntryMode', '');
    const posObj = _.get(transactionDetails, 'cardTransaction.pos', {});
    const isHaveCardPresentProperty = posObj.hasOwnProperty('cardPresent');
    const cardPresent = isHaveCardPresentProperty
        ? capitalizeFirstLetter(
              _.get(transactionDetails, 'cardTransaction.pos.cardPresent', '').toString(),
          )
        : '';

    return [
        {
            rows: [
                {
                    name: 'POS condition',
                    customComponentValue: posCondition && (
                        <ClickToCopy value={posCondition} dataTestId="value-pos-condition">
                            {posCondition}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'POS entry mode',
                    customComponentValue: posEntryMode && (
                        <ClickToCopy value={posEntryMode} dataTestId="value-pos-entry-mode">
                            {posEntryMode}
                        </ClickToCopy>
                    ),
                },
                {
                    name: 'Card present',
                    customComponentValue: cardPresent && (
                        <ClickToCopy value={cardPresent} dataTestId="value-card-present">
                            {cardPresent}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};

export const hasDataToDisplayInKeyValueComponent = (data: any[]): boolean => {
    return data.some((item) =>
        item.rows.some(
            (row: any) =>
                row.customComponentValue !== null &&
                row.customComponentValue !== undefined &&
                row.customComponentValue !== '',
        ),
    );
};
